import React from 'react';
import {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {useParams} from 'react-router-dom';
import _ from 'lodash';
import {Box, Container, Grid, Skeleton, Stack, Typography} from '@mui/material';
import {formatDistanceToNow} from 'date-fns';
import {useGetOneYouTubeProfileFeed} from '../../api/graphql_profile_feed';
import {useGetHistoryScoresOverTime} from '../../api/graphql_history_score_overtime';
import {VideoCard} from '../YouTube/VideoCard';
import {Sparkline} from '../YouTube/Sparkline';

interface Props {
  client: ApolloClient<NormalizedCacheObject>;
}

function FeedHeader({
  count,
  sparkSeries,
  loading,
}: {
  count: number;
  sparkSeries: number[];
  loading: boolean;
}): React.JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: {xs: 'flex-start', sm: 'center'},
        flexDirection: {xs: 'column', sm: 'row'},
        gap: 2,
        py: 3,
      }}
    >
      <Box sx={{flex: 1, minWidth: 0}}>
        <Typography sx={{fontSize: 22, fontWeight: 800, color: '#0F172A', lineHeight: 1.2}}>
          Recommendations
        </Typography>
        <Typography sx={{fontSize: 13, color: '#64748B', mt: 0.5}}>
          {loading
            ? 'Loading recommendations…'
            : `${count.toLocaleString()} video${count === 1 ? '' : 's'} that YouTube suggested in their feed`}
        </Typography>
      </Box>
      {sparkSeries.length > 0 && (
        <Box sx={{color: '#3448F0'}}>
          <Sparkline data={sparkSeries} width={140} height={32} stroke="#3448F0" />
        </Box>
      )}
    </Box>
  );
}

function GridSkeleton(): React.JSX.Element {
  return (
    <Grid container spacing={{xs: 2, md: 3}} columns={{xs: 4, sm: 8, md: 12}}>
      {Array.from({length: 12}).map((_, i) => (
        <Grid key={i} size={{xs: 6, sm: 4, md: 3, lg: 2}}>
          <Skeleton variant="rounded" height={180} />
        </Grid>
      ))}
    </Grid>
  );
}

export default function OneProfileFeed({client}: Props): React.JSX.Element {
  const {pid} = useParams();
  const {data, loading, error} = useGetOneYouTubeProfileFeed(client, pid as string);
  const {data: trend} = useGetHistoryScoresOverTime(client, pid as string);

  const sparkSeries = _.takeRight(
    _.sortBy(trend?.getHistoryScoresOverTime?.dailyScores ?? [], d => d.date).map(
      d => d.score ?? 0,
    ),
    7,
  );

  const videos = data?.getOneYouTubeProfileFeed ?? [];

  return (
    <Box sx={{minHeight: '100vh', bgcolor: '#F8FAFC'}}>
      <Container maxWidth="md" sx={{py: 0}}>
        <FeedHeader
          count={videos.length}
          sparkSeries={sparkSeries}
          loading={loading && !data}
        />

        {error && (
          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid #FECACA',
              bgcolor: '#FEF2F2',
              p: 3,
              mb: 3,
            }}
          >
            <Typography sx={{fontSize: 13, color: '#991B1B'}}>
              Couldn&rsquo;t load recommendations: {error.message}
            </Typography>
          </Box>
        )}

        {loading && !data && (
          <Box sx={{pb: 6}}>
            <GridSkeleton />
          </Box>
        )}

        {data && pid && videos.length === 0 && (
          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid #E2E8F0',
              bgcolor: '#FFFFFF',
              p: 4,
              textAlign: 'center',
              mb: 6,
            }}
          >
            <Typography sx={{fontSize: 14, color: '#64748B'}}>
              No recommendations recorded yet. They&rsquo;ll appear here as the desktop agent
              captures the feed.
            </Typography>
          </Box>
        )}

        {data && pid && videos.length > 0 && (
          <Box sx={{pb: 6}}>
            <Grid container spacing={{xs: 2, md: 3}} columns={{xs: 4, sm: 8, md: 12}}>
              {videos.map(video => {
                const recommendedAt =
                  video.recommended_on && video.recommended_on.length > 0
                    ? formatDistanceToNow(
                        new Date(video.recommended_on[video.recommended_on.length - 1]),
                        {addSuffix: true},
                      )
                    : undefined;
                return (
                  <Grid key={video.id} size={{xs: 6, sm: 4, md: 3, lg: 2}}>
                    <VideoCard
                      pid={pid}
                      client={client}
                      video={video}
                      recommendedAt={recommendedAt}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
}
